<script setup lang="ts">
import { subDays } from 'date-fns';
import { type Ref, ref } from 'vue';
import PlantApi from '@/data/plants/PlantApi.ts';
import type { DateRange } from '@/domain/DateRange.ts';
import Company from '@/domain/model/business/Company.ts';
import Meter from '@/domain/model/business/Meter.ts';
import Plant from '@/domain/model/business/Plant.ts';
import YieldPromiseStatus from '@/domain/model/business/YieldPromiseStatus.ts';
import {
  determineLackingYieldReasons,
  getYieldInsightsForMeters,
} from '@/domain/yield-insights/InsightsAggregator.ts';
import type LackingYieldReason from '@/domain/yield-insights/LackingYieldReason.ts';
import { useUiStore } from '@/stores/UiStore.ts';

const props = defineProps({
  company: { type: Company, required: true },
  period: { type: YieldPromiseStatus, required: true },
});

const mainLackingYieldReason: Ref<LackingYieldReason> = ref();

getMetersForPlant().then((meters) => getMainLackingYieldReason(meters));

async function getMetersForPlant() {
  const rsp = await new PlantApi().getSinglePlantMeters(
    props.company.getId(),
    props.period.getPlantId(),
  );
  return await rsp.data
    .filter((m) => !m.retired_on)
    .map((meterJson) => new Meter(meterJson));
}

function getMainLackingYieldReason(meters: Array<Meter>) {
  const plant = Plant.fromJson({ id: props.period.getPlantId() });
  const localeName = useUiStore().locale;
  const dateSelection = {
    dateRange: {
      startDate: props.period.getPeriodStart(),
      endDate: subDays(new Date(), 2),
    } as DateRange,
    periodType: 'month',
  };

  getYieldInsightsForMeters(props.company, plant, meters, dateSelection)
    .then((insightsPerMeter) => Array.from(insightsPerMeter.values()))
    .then((insightsPerMeter) => insightsPerMeter.flat(1))
    .then((yieldInsights) =>
      determineLackingYieldReasons(yieldInsights, localeName, false),
    )
    .then((result: Array<LackingYieldReason>) => {
      mainLackingYieldReason.value = result.reduce((prev, current) =>
        prev.lackingYieldSum < current.lackingYieldSum ? prev : current,
      );
    });
}
</script>

<template>
  <i18n-t
    v-if="mainLackingYieldReason"
    keypath="features.long_term.insight.reason"
  >
    <template #reason>
      <strong>{{ mainLackingYieldReason.displayName }}</strong>
    </template>
  </i18n-t>
  <Skeleton v-else width="30rem" />
</template>

<style scoped></style>
